import {PlayCircle} from '@mui/icons-material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {SPEED_FORMATS, toDisplaySpeed} from '../../../constants/common';
import {useAppSelector} from '../../../hooks/redux';
import {HazardEventLog, HazardEventLogListResponse} from '../../../interfaces/HazardAILog';
import {TimeOfDayQuery} from '../../../interfaces/HazardAITimeOfDay';
import DataGrid, {DataGridColumn} from '../../common/DataGrid';
import ModalFixed from '../../common/ModalFixed';
import {HazardDetectionMediaModal} from '../HazardAIDetectionLog/HazardDetectionMediaModal';

interface Props {
  open: boolean;
  date: string;
  hour: number;
  filterParams: TimeOfDayQuery;
  onClose: () => void;
}

const baseColumns: DataGridColumn<HazardEventLog>[] = [
  {
    field: 'type',
    width: 40,
    headerName: 'Detection Status',
    resizeable: true,
    sortable: true,
    renderHeader: () => (
      <Tooltip title="Detection Status">
        <FiberManualRecordIcon />
      </Tooltip>
    ),
    renderCell: ({row}) =>
      row.type === 'detection' ? (
        <Tooltip title="Valid Detection">
          <FiberManualRecordIcon color="success" />
        </Tooltip>
      ) : (
        <Tooltip title="False Detection">
          <FiberManualRecordIcon color="error" />
        </Tooltip>
      ),
  },
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    width: 160,
    resizeable: true,
    sortable: true,
  },
  {
    field: 'zone',
    headerName: 'Zone',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => <Box textTransform="capitalize">{row.zone}</Box>,
  },
  {
    field: 'object',
    headerName: 'Object',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => <Box textTransform="capitalize">{row.object}</Box>,
  },
  {
    field: 'confidence',
    headerName: 'Detection Confidence',
    width: 180,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => (
      <Box textTransform="capitalize">{row.confidence}</Box>
    ),
  },
  {
    field: 'raw_image',
    headerName: 'Detection Video',
    width: 140,
    resizeable: true,
    sortable: false,
    renderCell: () => null,
  },
  {
    field: 'distance',
    headerName: 'Detection Distance',
    width: 160,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.distance,
  },
  {
    field: 'speed',
    headerName: 'Speed',
    width: 80,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.speed,
  },
  {
    field: 'latitude',
    headerName: 'Latitude',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.latitude,
  },
  {
    field: 'longitude',
    headerName: 'Longitude',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.longitude,
  },
];

const buildColumns = (
  onPlayClick: (id: number) => void,
  speedFormat: (typeof SPEED_FORMATS)[number]
): DataGridColumn<HazardEventLog>[] =>
  baseColumns.map((col) => {
    if (col.field === 'raw_image') {
      return {
        ...col,
        renderCell: ({row}) => (
          <Box width="100%" display="flex" justifyContent="center">
            <IconButton onClick={() => onPlayClick(row.id)}>
              <PlayCircle />
            </IconButton>
          </Box>
        ),
      };
    }
    if (col.field === 'speed') {
      return {
        ...col,
        headerName: `Speed (${speedFormat})`,
        renderCell: ({row}) =>
          row.speed != null
            ? toDisplaySpeed(Number(row.speed), speedFormat)
            : null,
      };
    }
    return col;
  });

export const TimeOfDayDetectionsModal = ({
  open,
  date,
  hour,
  filterParams,
  onClose,
}: Props) => {
  const [mediaId, setMediaId] = useState<number | null>(null);
  const speedFormat = useAppSelector(({app}) => app.speedFormat);

  const [items, setItems] = useState<HazardEventLog[]>([]);
  const [count, setCount] = useState(0);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchDetections = useCallback(async () => {
    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<HazardEventLogListResponse>(
        `${apiBaseUrl}/hazard-ai/detection-time-of-day/detections`,
        {
          params: {
            ...filterParams,
            date,
            hour,
            page: 0,
            limit: 10000,
            order: 'timestamp',
            dir: 'DESC',
          },
        }
      );
      setItems(resp.data.items);
      setCount(resp.data.count);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_tod_det01');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  }, [date, hour, filterParams]);

  useEffect(() => {
    if (open) {
      fetchDetections();
    }
  }, [open, fetchDetections]);

  const hourStr = String(hour).padStart(2, '0');

  return (
    <>
      <ModalFixed open={open} width={Math.min(1100, window?.innerWidth - 60)}>
        <Box display="flex" flexDirection="column" height="80vh" p={2}>
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1}
            borderBottom="1px solid"
            borderColor="divider"
            flexShrink={0}
          >
            <Typography variant="h6" fontWeight="bold">
              {date} {hourStr}:00–{hourStr}:59 — Detections ({count})
            </Typography>
            <IconButton size="small" onClick={onClose}>
              ✕
            </IconButton>
          </Box>

          {/* Errors */}
          {fetchedErrors.map((err, idx) => (
            <Alert key={idx} severity="error" onClose={fetchDetections}>
              {err}
            </Alert>
          ))}

          {/* Loading */}
          {fetchedInProgress && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {/* Detection list */}
          {!fetchedInProgress && (
            <Box flex={1} overflow="hidden" position="relative">
              <DataGrid
                rows={items}
                columns={buildColumns(setMediaId, speedFormat)}
                size="small"
                pagination
                paginationMode="client"
              />
            </Box>
          )}
        </Box>
      </ModalFixed>

      {mediaId != null && (
        <HazardDetectionMediaModal
          open
          id={mediaId}
          onClose={() => setMediaId(null)}
        />
      )}
    </>
  );
};
